import React, { useMemo } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faSearch,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";

function Table({
  columns,
  data,
  filterInput,
  setFilterInput,
  pageCount,
  loading,
  handleEdit,
  handleDelete,
  onPageChange,
  currentPage,
  actionButtonLabel = "Add",
  showActionButton = false,
  onTrigger = () => {},
  onImportTrigger = () => {},
  className = "", // Accept className as a prop with a default empty string
}) {
  // Use data directly since filtering is done server-side
  // Client-side filtering removed to avoid conflicts with server-side pagination
  const filteredData = data;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: currentPage, pageSize: 10 },
      pageCount,
      manualPagination: true,
      sortBy: [{ id: 'name', desc: true }],
    },
    useSortBy,
    usePagination
  );

  // Use currentPage from props instead of internal state for manual pagination
  const actualPageIndex = currentPage || 0;
  const canGoPrevious = actualPageIndex > 0;
  const canGoNext = actualPageIndex < pageCount - 1;

  // Call the onPageChange when the page changes
  const handleGotoPage = (pageIndex) => {
    gotoPage(pageIndex);
    onPageChange(pageIndex); // Trigger the page change in the parent
  };

  // Handle previous page with parent callback
  const handlePreviousPage = () => {
    if (canGoPrevious) {
      const newPageIndex = actualPageIndex - 1;
      gotoPage(newPageIndex);
      onPageChange(newPageIndex);
    }
  };

  // Handle next page with parent callback
  const handleNextPage = () => {
    if (canGoNext) {
      const newPageIndex = actualPageIndex + 1;
      gotoPage(newPageIndex);
      onPageChange(newPageIndex);
    }
  };

  return (
    <div className="applications">
      <div className="d-flex justify-content-end mb-3">
        {showActionButton && actionButtonLabel === 'Add Community' && (
          <button
            type="button"
            onClick={onImportTrigger}
            className="btn btn-add-comm"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Import Communities
          </button>
        )}
        {showActionButton && (
          <button
            type="button"
            onClick={onTrigger}
            className="btn btn-add-comm mx-4"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            {actionButtonLabel}
          </button>
        )}
        <div
          className="input-group"
          style={{ width: "300px", position: "relative" }}
        >
          <input
            type="text"
            value={filterInput}
            onChange={(e) => setFilterInput(e.target.value)}
            className="form-control ps-5"
            placeholder="Search"
            style={{ backgroundColor: "transparent", borderColor: "#ccc" }}
          />
          <FontAwesomeIcon
            icon={faSearch}
            style={{
              position: "absolute",
              left: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#ccc",
              zIndex: 1,
            }}
          />
        </div>
      </div>

      <div className="outer">
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table
            {...getTableProps()}
            className={`table table-striped table-bordered ${className}`}
          >
          <thead>
  {headerGroups.map((headerGroup, headerIndex) => {
              const headerGroupProps = headerGroup.getHeaderGroupProps();
    const { key: headerGroupKey, ...restHeaderGroupProps } = headerGroupProps; // Extract `key`

              return (
                <tr key={headerGroupKey} {...restHeaderGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const columnProps = column.getHeaderProps(column.getSortByToggleProps());
          const { key: columnKey, ...restColumnProps } = columnProps; // Extract `key`

                    return (
                      <th key={columnKey} {...restColumnProps}>
                        {column.render("Header")}
                        {!column.disableSortBy && (
                          <span>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <FontAwesomeIcon icon={faSortDown} />
                              ) : (
                                <FontAwesomeIcon icon={faSortUp} />
                              )
                            ) : (
                              <FontAwesomeIcon icon={faSort} />
                            )}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>


          <tbody {...getTableBodyProps()}>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-5">
                  <div className="d-flex justify-content-center align-items-center flex-column my-3">
                    <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="mt-2 text-muted small">Loading records...</span>
                  </div>
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No Data Available
                </td>
              </tr>
            ) : (
    page.map((row, rowIndex) => {
                prepareRow(row);
                const rowProps = row.getRowProps();
      const { key: rowKey, ...restRowProps } = rowProps; // Extract `key`

                return (
                  <tr key={rowKey} {...restRowProps}>
          {row.cells.map((cell, cellIndex) => {
                      const cellProps = cell.getCellProps();
            const { key: cellKey, ...restCellProps } = cellProps; // Extract `key`

                      return (
                        <td key={cellKey} {...restCellProps}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>

        {filteredData.length > 0 && (
          <div className="pagination d-flex justify-content-end">
            <button
              onClick={handlePreviousPage}
              disabled={!canGoPrevious}
              className="btn btn-secondary"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <span>
              {Array.from({ length: pageCount }, (_, index) => (
                <button
                  key={index}
                  onClick={() => handleGotoPage(index)}
                  className={`btn btn-sm mx-1 ${currentPage === index ? "btn-primary active" : "btn-light"}`}
                  aria-current={currentPage === index ? "page" : undefined}
                >
                  {index + 1}
                </button>
              ))}
            </span>

            <button
              onClick={handleNextPage}
              disabled={!canGoNext}
              className="btn btn-secondary"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Table;
